import { Injectable } from "@angular/core";
import { Observable, from, of, throwError, timer, zip } from "rxjs";
import { FileSystemAccessService } from "../../_services/file-system-access.service";
import { catchError, map, switchMap, switchMapTo, take } from "rxjs/operators";
import {
  IplexDeviceResponse,
  IplexDeviceRequest,
  StartHttpsPram,
  RequestWifiSettingsPram,
  ResponseHttpsStart,
  ResponseWifiSettingsPram
} from "../models/iplex-device/iplex-device";
import { FileStat } from "webdav";
import { FILE_SYSTEM, IPC_CHANNEL, OFFLINE_REGEX } from "../electron.constants";
import { SpinnerService } from "../../_services/spinner.service";
import { IplexDeviceConnectModeService } from "./iplex-device-connect-mode.service";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { CommonConfirmDialogComponent } from "../../common-dialog/common-confirm-dialog.component";
import { ElectronService } from "../../electron.service";

export namespace CGI_COMMANDS {
  export const Get_Status = "GetStatus";

  export const Start_Https_P2P = "StartHttpsP2P";
  export const Stop_Https_P2P = "StopHttpsP2P";

  export const Start_SDcard_WebDAV = "StartSdcardWebdav";
  export const Stop_SDcard_WebDAV = "StopSdcardWebdav";

  export const Get_WiFi_Settings = "GetWiFiSettings";
  export const Set_WiFi_Settings = "SetWiFiSettings";
}

@Injectable({
  providedIn: "root"
})
export class IplexDeviceSyncService {
  readonly iplexDeviceUser = "EvidentViSOLUser";
  readonly httpsPassPhrase = "S~pj;2M6O0w$]jK7";
  readonly webDavPassPhrase = "P02wD-5_i/jKdu&R";
  readonly IGNORE_DIRECTORIES = ["adlog", "maintenance", "update", "language", "preset", "battery", "logo", "system volume information"];

  constructor(
    private fileSysAccessService: FileSystemAccessService,
    private _electronService: ElectronService,
    private spinner: SpinnerService,
    private snackbar: CommonSnackBarService,
    private translate: TranslateService,
    private iplexDeviceConnectModeService: IplexDeviceConnectModeService,
    private dialog: MatDialog
  ) {}

  public getStatus() {
    const req = new IplexDeviceRequest();
    req.Request.Command = CGI_COMMANDS.Get_Status;
    return this.postIplexDevice(req);
  }

  public onHttpsConnectMode(clientName: string) {
    this.spinner.show(true);
    setTimeout(() => {
      this.startHttpsP2P(clientName).subscribe(
        (result) => {
          if (result && result.Response.Result === "Success") {
            if (result.Response.Data.Consent === "Yes") {
              this.iplexDeviceConnectModeService.setHttpsMode(true);
              this.snackbar.open(this.translate.instant("iplexDeviceConnectModeService.successConnect"));
            } else {
              this.iplexDeviceConnectModeService.setHttpsMode(false);
              this.snackbar.open(this.translate.instant("iplexDeviceConnectModeService.failedConnect"));
            }
          } else {
            this.iplexDeviceConnectModeService.setHttpsMode(false);
            this.snackbar.open(this.translate.instant("iplexDeviceConnectModeService.failedConnect"));
          }

          this.spinner.hide(true);
        },
        (error) => {
          console.log(error);
          this.iplexDeviceConnectModeService.setHttpsMode(false);
          this.snackbar.open(this.translate.instant("iplexDeviceConnectModeService.failedConnect"));
          this.spinner.hide(true);
        }
      );
    }, 500);
  }

  public offHttpsConnectMode() {
    this.spinner.show(true);
    this.stopHttpsP2P().subscribe(
      (result) => {
        if (result && result.Response.Result === "Success") {
          this.iplexDeviceConnectModeService.setHttpsMode(false);
          this.snackbar.open(this.translate.instant("iplexDeviceConnectModeService.successDisconnect"));
        } else {
          this.iplexDeviceConnectModeService.setHttpsMode(false);
        }
        this.spinner.hide(true);
      },
      (error) => {
        console.log(error);
        this.iplexDeviceConnectModeService.setHttpsMode(false);
        this.spinner.hide(true);
      }
    );
  }

  /** HTTPS通信開始
   * @returns Observable
   */
  public startHttpsP2P(clientName: string): Observable<IplexDeviceResponse<ResponseHttpsStart>> {
    const req = new IplexDeviceRequest();
    req.Request.Command = CGI_COMMANDS.Start_Https_P2P;

    const params = new StartHttpsPram();
    params.User = this.iplexDeviceUser;
    params.PassPhrase = this.httpsPassPhrase;
    params.Client = clientName;
    req.Request.Params = params;

    return this.postIplexDevice(req);
  }

  /** HTTPS通信停止
   * @returns Observable
   */
  public stopHttpsP2P(): Observable<IplexDeviceResponse<any>> {
    const req = new IplexDeviceRequest();
    req.Request.Command = CGI_COMMANDS.Stop_Https_P2P;
    const sendSyncOf = of(this._electronService.sendSyncCommand(IPC_CHANNEL.HTTPS_TO_IPLEX.HTTPS, req));
    return timer(50)
      .pipe(switchMapTo(sendSyncOf))
      .pipe(take(1))
      .pipe(
        map((response) => {
          if (response instanceof Error) {
            throw response;
          }
          return response;
        })
      );
  }

  /** Wifi設定取得
   * @returns Observable
   */
  public getWiFiSettings(): Observable<IplexDeviceResponse<ResponseWifiSettingsPram>> {
    const req = new IplexDeviceRequest();
    req.Request.Command = CGI_COMMANDS.Get_WiFi_Settings;

    const params = new StartHttpsPram();
    params.User = this.iplexDeviceUser;
    params.PassPhrase = this.webDavPassPhrase;
    req.Request.Params = params;

    return this.postIplexDevice(req);
  }

  /** Wifi設定更新
   * @param  {RequestWifiSettingsPram} param 設定値オブジェクト
   * @returns Observable
   */
  public postWiFiSettings(param: RequestWifiSettingsPram): Observable<IplexDeviceResponse<any>> {
    const req = new IplexDeviceRequest();
    req.Request.Command = CGI_COMMANDS.Set_WiFi_Settings;
    req.Request.Params = param;
    return this.postIplexDevice(req);
  }

  public onWebDAVConnectMode() {
    return this.startSdCardWebDAV().pipe(
      map(
        (result) => {
          const resResult = result?.Response.Result;
          if (resResult === "Success") {
            return this.iplexDeviceConnectModeService.setWebDAVMode(true);
          } else if (resResult === "DisableError" || resResult === "ImpossibleError") {
            this.dialog.open(CommonConfirmDialogComponent, {
              disableClose: true,
              data: {
                firstMessage: this.translate.instant("iplexDeviceConnectModeService.failedWebDAVStartMessage1"),
                secondMessage: this.translate.instant("iplexDeviceConnectModeService.failedWebDAVStartMessage2"),
                dialogType: "information",
                subMessages: [
                  this.translate.instant("iplexDeviceConnectModeService.failedWebDAVStartSubMessage1"),
                  this.translate.instant("iplexDeviceConnectModeService.failedWebDAVStartSubMessage2"),
                  this.translate.instant("iplexDeviceConnectModeService.failedWebDAVStartSubMessage3")
                ]
              }
            });
            return this.iplexDeviceConnectModeService.setWebDAVMode(false);
          } else {
            throwError("Unhandled status WevDAB Connection.");
          }
        },
        (error) => {
          console.log(error);
          return this.iplexDeviceConnectModeService.setWebDAVMode(false);
        }
      )
    );
  }

  public offWebDAVConnectMode() {
    return this.stopSdCardWebDAV().pipe(
      map(
        (result) => {
          if (!result || result.Response.Result !== "Success") {
            throw new Error(this.translate.instant("iplexDeviceConnectModeService.failedConnect"));
          }
          this.iplexDeviceConnectModeService.setWebDAVMode(false);
        },
        (error) => {
          console.log(error);
          this.iplexDeviceConnectModeService.setWebDAVMode(false);
        }
      )
    );
  }

  /** WebDAV通信開始
   * @returns Observable
   */
  public startSdCardWebDAV(): Observable<IplexDeviceResponse<any>> {
    const req = new IplexDeviceRequest();
    req.Request.Command = CGI_COMMANDS.Start_SDcard_WebDAV;

    const params = new StartHttpsPram();
    params.User = this.iplexDeviceUser;
    params.PassPhrase = this.webDavPassPhrase;
    req.Request.Params = params;

    return this.postIplexDevice(req);
  }

  /** WebDAV通信停止
   * @returns Observable
   */
  public stopSdCardWebDAV(): Observable<IplexDeviceResponse<any>> {
    const req = new IplexDeviceRequest();
    req.Request.Command = CGI_COMMANDS.Stop_SDcard_WebDAV;
    return this.postIplexDevice(req);
  }

  /** SDカード フォルダー構成取得 (WebDAV)
   * @param  {string} directoryPath フォルダーパス
   * @returns Observable
   */
  getDirectoriesInSDcard(directoryPath: string): Observable<FileStat[]> {
    return this.ipcSendSync(IPC_CHANNEL.WEB_DAV.GET_CONTENTS, directoryPath).pipe(
      map((contents: FileStat[]) => {
        if (!contents || contents.length < 1) {
          return [];
        }

        return contents
          .filter((content) => content.type === "directory")
          .filter((content) => !this.IGNORE_DIRECTORIES.some((_) => _ === content.basename.toLowerCase()))
          .filter((content) => OFFLINE_REGEX.File_Stat_Basename.test(content.basename));
      })
    );
  }

  /** SDカード 全ファイル構成取得 (WebDAV)
   * 指定フォルダパス以下のファイル構成を再帰的に取得する
   * @param  {string} directoryPath フォルダーパス
   * @returns Observable
   */
  getALLContentsFromInSDcard(directoryPath: string) {
    let baseDirectoryFiles: FileStat[] = [];
    return this.getContentsFromInSDcard(directoryPath)
      .pipe(map((contents: FileStat[]) => contents.filter((cont) => OFFLINE_REGEX.File_Stat_Basename.test(cont.basename))))
      .pipe(
        switchMap((contents) => {
          if (!contents || contents.length < 1) {
            return of([] as FileStat[][]);
          }

          const directories = contents.filter((cont) => cont.type === "directory");
          if (directories.length > 0) {
            baseDirectoryFiles = contents;
            const getContentsObs = directories.map((dir) => {
              return this.getContentsFromInSDcard(dir.filename).pipe(
                map((contents: FileStat[]) => contents.filter((cont) => OFFLINE_REGEX.File_Stat_Basename.test(cont.basename)))
              );
            });
            return zip(...getContentsObs);
          }

          return of([contents]);
        })
      )
      .pipe(
        map((contents) => {
          let result: FileStat[] = baseDirectoryFiles;
          contents.filter((cont) => cont !== null).forEach((cont) => (result = result.concat(cont)));
          return result;
        })
      );
  }

  /** SDカード フォルダー直下ファイル構成取得 (WebDAV)
   * @param  {string} directoryPath フォルダーパス
   * @returns Observable
   */
  getContentsFromInSDcard(directoryPath: string): Observable<FileStat[]> {
    return this.ipcSendSync(IPC_CHANNEL.WEB_DAV.GET_CONTENTS, directoryPath);
  }

  /** SDカード フォルダー作成要求 (WebDAV)
   * @param  {string} directoryPath フォルダーパス
   * @returns Observable
   */
  public postDirectoryInSDcard(directoryPath: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.WEB_DAV.PUT_DIRECTORY, directoryPath);
  }

  /** SDカード ファイル削除要求 (WebDAV)
   * @param  {string} contentPath ファイルパス
   * @returns Observable
   */
  public deleteContentInSDcard(contentPath: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.WEB_DAV.DELETE_FILE, contentPath);
  }

  /** SDカード ファイル取得要求 (WebDAV)
   * @param  {string} contentPath SDカード内の取得するファイルパス
   */
  public getFileFromSDcardStream(contentPath: string, destFilePath = null, enableSendEvent = true): Observable<boolean> {
    if (destFilePath) {
      return this.ipcSendSync(IPC_CHANNEL.WEB_DAV.SAVE_FILE, contentPath, destFilePath, enableSendEvent);
    }

    return from(this.fileSysAccessService.getSaveBlobFolderPath()).pipe(
      switchMap((blobFolderPath) => {
        const destFolderPath = blobFolderPath + FILE_SYSTEM.PATH.TEMP;
        return this.ipcSendSync(IPC_CHANNEL.WEB_DAV.SAVE_FILE, contentPath, destFolderPath + contentPath, enableSendEvent);
      })
    );
  }

  /** SDカード ファイル送信要求
   * @param  {string} srcPath 送信するファイルのパス
   * @param  {string} destPath SDカード内の保存先のパス
   */
  public postFileForSDcard(srcPath: string, destPath: string): Observable<boolean> {
    return from(this.fileSysAccessService.getSaveBlobFolderPath()).pipe(
      switchMap((blobFolderPath) => this.ipcSendSync(IPC_CHANNEL.WEB_DAV.PUT_FILE, blobFolderPath + srcPath, destPath))
    );
  }

  public cancelFileTransfer() {
    return this.ipcSendSync(IPC_CHANNEL.WEB_DAV.SAVE_CANCEL);
  }

  public postIplexDevice(req: IplexDeviceRequest) {
    return this.ipcSendSync(IPC_CHANNEL.HTTPS_TO_IPLEX.HTTPS, req);
  }

  private ipcSendSync(channel: string, ...args: any[]) {
    const sendSyncOf = of(this._electronService.sendSyncCommand(channel, ...args));
    return timer(50)
      .pipe(switchMapTo(sendSyncOf))
      .pipe(take(1))
      .pipe(
        map((response) => {
          if (response instanceof Error) {
            throw response;
          }
          return response;
        })
      )
      .pipe(
        catchError((error: any) => {
          console.log(error);
          this.iplexDeviceConnectModeService.notifyLinkErrors(false);
          this.spinner.hide(true);
          return throwError(error);
        })
      );
  }
}
